import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Button, TextView } from '@atoms';
import { DateRangePicker, Range } from 'react-date-range';
import Popover from '@mui/material/Popover';
import EventIcon from '@mui/icons-material/EventOutlined';
import { DateTime } from 'luxon';
import { materialTheme } from '@particles';

interface HeaderProps {
  start: Date;
  end: Date;
  subComponent?: React.ReactNode;
  csvDownload?: boolean;
  onSelectRange?: (start: Date, end: Date) => void;
}

const Header: React.FC<HeaderProps> = (props: HeaderProps) => {
  const { start, end, onSelectRange, csvDownload, subComponent } = props;
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [downloading, setDownloading] = React.useState<boolean>(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const [range, setRange] = useState<Range[]>([
    {
      startDate: start,
      endDate: end,
      key: 'selection'
    }
  ]);

  /**
   * Both boundaries snap to whole days. `end` was always pushed to 23:59:59,
   * but `start` kept whatever time the picker (or the parent's default seed)
   * carried, so a range labelled "last 7 days" actually covered 6-and-a-bit:
   * it opened mid-way through the first day and ran to the end of the last.
   * On Srts-backed tiles — whose rows are stamped at midnight, one per
   * aggregated day — that dropped the first day's row outright rather than
   * merely clipping it.
   */
  const normalizeRange = (from?: Date, to?: Date): [Date, Date] => {
    const fallback = new Date();
    const newStart = new Date(from ?? fallback);
    newStart.setHours(0, 0, 0, 0);
    const newEnd = new Date(to ?? fallback);
    newEnd.setHours(23, 59, 59, 999);
    return [newStart, newEnd];
  };

  const isFirstRangeSync = useRef(true);
  useEffect(() => {
    // Skip the mount fire. Otherwise we'd normalize end to 23:59:59 and hand
    // the parent a new Date reference, triggering a refetch storm on every
    // dashboard that consumes this header before the user has done anything.
    if (isFirstRangeSync.current) {
      isFirstRangeSync.current = false;
      return;
    }
    const [newStart, newEnd] = normalizeRange(range[0]?.startDate, range[0]?.endDate);
    onSelectRange && onSelectRange(newStart, newEnd);
  }, [range]);

  const downloadFile = async () => {
    setDownloading(true);
    const token = localStorage.getItem('token');
    const apiBase = process.env.REACT_APP_API_BASE;

    // Same normalization as the on-screen range, or the CSV covers a
    // different window than the dashboard it was exported from.
    const [rangeStart, rangeEnd] = normalizeRange(range[0]?.startDate, range[0]?.endDate);
    const startDate = DateTime.fromJSDate(rangeStart).toISO({
      suppressMilliseconds: true,
      includeOffset: false
    });
    const endDate = DateTime.fromJSDate(rangeEnd).toISO({
      suppressMilliseconds: true,
      includeOffset: false
    });

    const urlApi = `${apiBase}/export-csv?fromDate=${startDate}&toDate=${endDate}`;

    const zone: string[] = DateTime.now().toFormat('ZZ').split(':');
    const hour = Number(zone[0]);
    const mint = Number(zone[1]);
    const totalMint: number = hour * 60 + mint;

    const fetchOptions: RequestInit = {
      headers: {
        Authorization: `Bearer ${token ?? ''}`,
        'Content-Type': 'application/json',
        timezone: `${totalMint}`
      },
      method: 'GET'
    };

    const response: Response = await fetch(urlApi, fetchOptions);

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'DashboardReport.xlsx');
    document.body.appendChild(link);
    link.click();
    setDownloading(false);
  };

  return (
    <Box sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center' }}>
      <Box sx={{ flex: 1 }} />
      <TextView $fontWeight={400} $fontSize={14} mr={1}>
        Filters
      </TextView>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}>
        <DateRangePicker
          onChange={(item) => setRange([item.selection])}
          moveRangeOnFirstSelection={false}
          months={2}
          ranges={range}
          direction="horizontal"
        />
      </Popover>

      <Button
        aria-describedby={id}
        sx={{ paddingLeft: 5, color: materialTheme.palette.text.primary, mr: 2 }}
        startIcon={<EventIcon />}
        variant={'outlined'}
        onClick={handleClick}>
        <TextView $fontWeight={400}>
          {DateTime.fromJSDate(range[0].startDate ? range[0]?.startDate : new Date()).toFormat(
            'LLLL, dd yyyy'
          )}
          {` - `}
          {DateTime.fromJSDate(range[0].endDate ? range[0]?.endDate : new Date()).toFormat(
            'LLLL, dd yyyy'
          )}
        </TextView>
      </Button>
      {subComponent && <>{subComponent}</>}
      {csvDownload && <Button label="Download" onClick={downloadFile} loading={downloading} />}
    </Box>
  );
};
export default Header;
